// The live transcript on a call's page (calls round): what calls.liveTranscript gives, and how its lines
// are drawn (docs/specs/live-transcripts.md, "Reading it"). calls.liveTranscript {callId, after?}
// answers the final lines after `after` (pass the last read's `next`), and every line still being said
// (partial, at most one a side, whole on every read). The office stream's call_transcript.lines_added,
// its entity the call, says new lines are there. A company with live transcripts off, or a call never
// transcribed, answers no lines: the page says "No transcript yet" while the call runs. After the call
// the lines are its saved transcript, read for a quarter hour by the office's page and then only by
// owner and managers (recordings.play, AD-9). Kept out of the component so it's tested without a browser.
import type { TranscriptLine, TranscriptStatus } from '@flowology/ui-web/fl';

/** One line as calls.liveTranscript gives it: `at` is seconds into the call. */
export interface LiveLine {
  id: string;
  seq: number;
  speaker: 'office' | 'caller';
  /** The caller's contact or who took the call; "Caller" or "Office" when nobody is known. */
  name: string;
  at: number;
  text: string;
  final: boolean;
}

/** calls.liveTranscript's answer. */
export interface LiveRead {
  callId: string;
  /** Still being transcribed. */
  live: boolean;
  lines: LiveLine[];
  partial: LiveLine[];
  /** Pass as `after` on the next read. */
  next: number;
  /** More final lines than one read carries: read again with `next` at once. */
  more: boolean;
}

/** The office stream's word that a call's transcript changed (its entity is the call). One rule. */
export const TRANSCRIPT_EVENTS = /^call_transcript\.lines_added$/;

/** With no word from the stream for this long while it's live, read anyway (the stream may be down). */
export const POLL_MS = 10_000;

/** What the page holds: the final lines in order, what's still being said, and where to read from. */
export interface LiveHeld {
  /** null until calls.liveTranscript first answers. */
  live: boolean | null;
  lines: LiveLine[];
  partial: LiveLine[];
  next: number;
}

export const NOTHING_HELD: LiveHeld = { live: null, lines: [], partial: [], next: 0 };

/**
 * A read taken in: its final lines after those held (one held already isn't added twice), in seq
 * order; what's still being said replaced whole.
 */
export function takeRead(held: LiveHeld, r: LiveRead): LiveHeld {
  const have = new Set(held.lines.map((l) => l.id));
  const lines = [...held.lines, ...r.lines.filter((l) => l.final && !have.has(l.id))].sort(
    (a, b) => a.seq - b.seq,
  );
  return { live: r.live, lines, partial: r.partial, next: Math.max(held.next, r.next) };
}

/** "0:16", "12:03": seconds into the call. */
export function intoCall(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** The lines as LiveTranscript draws them: the office's set apart, each by its speaker's name. */
export function drawnLines(lines: readonly LiveLine[]): TranscriptLine[] {
  return lines.map((l) => ({
    who: l.name || (l.speaker === 'office' ? 'Office' : 'Caller'),
    isMe: l.speaker === 'office',
    at: intoCall(l.at),
    text: l.text,
  }));
}

/**
 * How it stands. Not answered yet (or the read failed): "No transcript yet". Being transcribed: live,
 * or starting before anything is said. Not being transcribed: done with lines; with none, "No
 * transcript yet" while the call still runs (it isn't transcribed, or hasn't started), and none for
 * this call once it's over.
 */
export function transcriptStatus(h: LiveHeld, callRunning: boolean): TranscriptStatus {
  const said = h.lines.length + h.partial.length > 0;
  if (h.live === null) return 'pending';
  if (h.live) return said ? 'live' : 'starting';
  if (h.lines.length) return 'done';
  return callRunning ? 'pending' : 'unavailable';
}

/** Who's on it, after the state: the names that have spoken, in the order they first did. */
export function peopleOf(lines: readonly LiveLine[]): string | undefined {
  const names = [...new Set(lines.map((l) => l.name).filter(Boolean))];
  return names.length ? names.join(', ') : undefined;
}
